import { ClockIcon } from 'assets/icons';
import AssignMenu from 'components/crm/ui/AssignMenu';
import { isDeletedGroupLink, markDeletedGroup, useLinkLabel } from 'components/record/deletedGroup';
import { kanbanDateText } from 'helper/taskDates';
import { rowLinks } from 'helper/taskLinks';
import useText from 'hooks/useText';
import LINK_ICONS from './linkIcons';
import TaskTypeBadge from './TaskTypeBadge';

/**
 * A task of the kanban: its title and assignee, its company, its type and its
 * due day (in red when late). It is dragged to another column; a click opens
 * it. `data-id` lets the board make it glide (hooks/useFlipLayout).
 */
const TaskKanbanCard = ({
  row,
  dragged,
  team,
  meId,
  onDragStart,
  onDragEnd,
  onOpen,
  onAssign,
}) => {
  const { text } = useText('tasks');
  const linkLabel = useLinkLabel();
  const late = row.late && !row.doneAt;
  const now = new Date();
  const dateTone = late ? 'text-red-600' : 'text-gray-600';
  // A task the user may not edit stays in its column, as an appointment or a
  // callback; a row without rights (the mock) allows everything.
  const isTask = row.kind === 'task';
  const canEdit = isTask && row.access?.canEdit !== false;
  // Its first record, then how many more.
  const links = rowLinks(row).map(markDeletedGroup(row));
  const place = links.length
    ? { Icon: LINK_ICONS[links[0].type], name: linkLabel(links[0]), deleted: isDeletedGroupLink(links[0]), more: links.length - 1 }
    : null;
  return (
    <article
      data-id={row.key}
      draggable={canEdit}
      onDragStart={(event) =>
        canEdit ? onDragStart(event, row) : event.preventDefault()
      }
      onDragEnd={onDragEnd}
      onClick={() => onOpen(row)}
      className={`cursor-pointer rounded-lg border border-gray-200 bg-white p-3 transition hover:border-gray-300 ${
        dragged ? 'opacity-50' : ''
      }`}
    >
      {/* The company sits right under the title, in its column: the avatar
        beside them, taller than a line, no longer pushes it down. */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p
            className={`text-sm font-medium ${row.doneAt ? 'text-gray-400 line-through' : 'text-gray-900'}`}
          >
            {row.title}
          </p>
          {place && (
            <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-gray-600">
              <span className="flex shrink-0">
                <place.Icon wh={16} color="currentColor" />
              </span>
              <span className={`truncate ${place.deleted ? 'text-gray-400' : ''}`}>{place.name}</span>
              {place.more > 0 && <span className="shrink-0">{text('moreLinks', { count: place.more })}</span>}
            </p>
          )}
        </div>
        {isTask && (
          <span
            className="flex shrink-0"
            onClick={(event) => event.stopPropagation()}
          >
            <AssignMenu
              value={row.assigneeId}
              people={team}
              meId={meId}
              readOnly={row.access?.canAssign === false}
              onChange={(assigneeId) => onAssign(row, assigneeId)}
            />
          </span>
        )}
      </div>
      {/* The type on the left, the due day on the right, on one line: the date is cut rather than going under the type. */}
      <div className="mt-2 flex items-center justify-between gap-1.5">
        <span className="shrink-0">
          <TaskTypeBadge row={row} />
        </span>
        <span title={kanbanDateText(row, now)} className={`flex min-w-0 items-center gap-1 text-xs ${dateTone}`}>
          <span className="flex shrink-0">
            <ClockIcon wh={14} color="currentColor" />
          </span>
          <span className="truncate">{kanbanDateText(row, now)}</span>
        </span>
      </div>
    </article>
  );
};

export default TaskKanbanCard;
